import React, { createContext, useContext, useState, useEffect, useRef, Suspense, lazy } from 'react';
import log from 'loglevel';
import Utils from '../utils/Utils';
import Sdk from '../sdk/Sdk';
import Inner from './Inner';
import Controls from './Controls';
import Translation from '../utils/Translation';
import { Autoplay, State, Events, SizePlayerEvents, ToggleControlsChangedEvents } from '../types/player';
import { callbackify } from 'util';

interface PlayerContextType {
  sdk: Sdk;
}

export const Context = createContext<PlayerContextType | undefined>(undefined);

export const Provider = (props: { sdk: Sdk }) => {
  const { sdk } = props;
  const translation = Translation.getInstance();
  const [uniqueId] = useState(Utils.randomString(10));
  const [size, setSize] = useState(null as any);
  const [style, setStyle] = useState({});
  const [inactivity, setInactivity] = useState(false);
  let currentSize = size;

  const t = useRef<NodeJS.Timeout | null>(null);
  const inactivityRef = useRef(inactivity);
  const controlsVisibility = useRef<ToggleControlsChangedEvents | null>(null);

  useEffect(() => {
    inactivityRef.current = inactivity;
  }, [inactivity]);

  useEffect(() => {
    let el = document.getElementById(`${sdk.getClassNamePrefix()}-player-${uniqueId}`);
    const events = ['load', 'mousemove', 'mousedown', 'touchstart', 'touchmove', 'click', 'keydown', 'scroll'];
    let lastExec = 0;
    const delay = 200;

    let style = {};
    let resetTimer: () => void;
    let RemoveInactivity: () => void;
    const width = sdk.options().getWidth();
    if (width) style = { ...style, width: width };
    const height = sdk.options().getHeight();
    if (height) style = { ...style, height: height };
    setStyle(style);

    setSize(sdk.getSize());
    sdk.on(Events.CHANGE_SIZE, (newSize: SizePlayerEvents) => {
      let newStyle = {
        ...style,
        ...{
          '--player-height': newSize.height + 'px',
          '--player-width': newSize.width + 'px',
        },
      };
      if (newSize.size != currentSize) {
        currentSize = newSize.size;
        setSize(newSize.size);
      }
      if (sdk.options().getRatio() !== -1 && !sdk.options().getWidth() && !sdk.options().getHeight()) {
        newStyle = {
          ...newStyle,
          ...{
            '--player-height': newSize.width / sdk.options().getRatio() + 'px',
            height: `calc(${newSize.width}px / ${sdk.options().getRatio()})`,
          },
        };
        //@ts-ignore
        //document.querySelector('.blastream-player-inner').style.height = `calc(${newSize.width}px / ${sdk.options().getRatio()})`;
      }
      setStyle(newStyle);
    });

    sdk.on(Events.TOGGLE_CONTROLS_CHANGED, (status: ToggleControlsChangedEvents) => {
      controlsVisibility.current = status;
    });
    if ((sdk.options().getControls().getSettings().hideAfterInactivityTimeout ?? 0) > 0) {
      if (el) {
        el.addEventListener(
          'touchmove',
          function (event) {
            event.preventDefault();
          },
          { passive: false },
        );
      }

      const toggleControlsChanged = (status: ToggleControlsChangedEvents) => {
        if (controlsVisibility.current === null) return;
        if (controlsVisibility.current != status) {
          controlsVisibility.current = status;
          sdk.toggleControlsChanged(status);
        }
      };
      resetTimer = () => {
        const now = Date.now();
        if (now - lastExec < delay) return;
        lastExec = now;

        //console.log('ResetTimer');
        if (inactivityRef.current === true) {
          //console.log('inactivity from  set false');
          toggleControlsChanged(ToggleControlsChangedEvents.SHOW);
          setInactivity(false);
          inactivityRef.current = false;
        }

        if (t.current) clearTimeout(t.current);
        t.current = setTimeout(() => {
          if (inactivityRef.current === false) {
            //console.log('inactivity set true');
            toggleControlsChanged(ToggleControlsChangedEvents.HIDE);
            setInactivity(true);
            inactivityRef.current = true;
          }
        }, sdk.options().getControls().getSettings().hideAfterInactivityTimeout);
      };

      RemoveInactivity = () => {
        //console.log('check inactivity from mouve over');
        if (inactivityRef.current === true) {
          //console.log('inactivity set false from mouve over');
          toggleControlsChanged(ToggleControlsChangedEvents.SHOW);
          setInactivity(false);
          inactivityRef.current = false;
        }
      };

      events.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));

      if (el) {
        el.addEventListener('mouseover', RemoveInactivity, { passive: true });
      }
    }
    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      if (el) {
        el.removeEventListener('mouseover', RemoveInactivity);
      }
      if (t.current) clearTimeout(t.current);
    };
  }, [sdk]);

  useEffect(() => {
    const loadTranslations = async () => {
      let lang = sdk.getViewerLanguage();
      if (!lang) return;

      await translation.load(lang as string, sdk.options().getLocale()?.translations, sdk.options().getLocale()?.fallback_lang as string);
    };

    loadTranslations();
  }, [sdk.options().getLocale()]);

  console.log('re render with inactivity = ', inactivity);
  return (
    <Context.Provider
      value={{
        sdk,
      }}
    >
      <div
        style={style}
        id={`${sdk.getClassNamePrefix()}-player-${uniqueId}`}
        className={`
          ${sdk.getClassNamePrefix()}-player 
          ${sdk.getClassNamePrefix()}-player${sdk.options().getFit() ? '-fit-' + sdk.options().getFit() : ''}
          ${sdk.getClassNamePrefix()}-player-device-${sdk.getUserAgent().getDeviceType().toLocaleLowerCase()}
          ${sdk.getClassNamePrefix()}-player-os-${sdk.getUserAgent().getOS().toLocaleLowerCase()}
          ${size != '' ? sdk.getClassNamePrefix() + '-player-size-' + size : ''}
          ${inactivity === true ? sdk.getClassNamePrefix() + '-player-inactivity' : ''}
          ${sdk.options().isBackground() === true ? sdk.getClassNamePrefix() + '-player-background' : ''}
          `}
      >
        <Inner />
        {sdk.options().getControls()?.isShow() !== false && <Controls />}
      </div>
    </Context.Provider>
  );
};

export const usePlayerContext = (): PlayerContextType => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('usePlayerContext must be used within a Provider');
  }
  return context;
};
